/**
 * A card whose body folds away under a clickable heading.
 *
 * The body is hidden, never removed: its controls keep their values and stay readable by whatever
 * owns them, so a page can fold a section that holds unsaved edits and still save them.
 *
 * The heading is its own control rather than `Card.onActivate`, because a click anywhere in an open
 * body - on a label, between two boxes - must not fold the section someone is typing into.
 */
import { Control, type Color, type ControlOptions, type RenderContext, type Size, type Theme } from 'webwin';
import { crm } from '../theme/CrmTheme.js';
import type { Card } from './Card.js';
import { card, FONT, GAP, relayout, vstack } from './layout.js';

const HEADER_HEIGHT = 30;
const CHEVRON = 10;

interface HeaderOptions extends ControlOptions {
  title: string;
  onToggle: () => void;
}

class CollapsibleHeader extends Control {
  readonly #title: string;
  summary = '';
  expanded = false;

  constructor(options: HeaderOptions) {
    super({ height: HEADER_HEIGHT, hAlign: 'stretch', cursor: 'pointer', tabStop: true, ...options });
    this.#title = options.title;
    this.on('click', () => options.onToggle());
    this.on('keydown', e => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.handled = true;
      options.onToggle();
    });
  }

  override preferredSize(available: Size): Size {
    return { width: Number.isFinite(available.width) ? available.width : this.width, height: HEADER_HEIGHT };
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const c = crm(theme);
    const r = this.localRect;
    const mid = r.y + r.height / 2;
    const ink: Color = this.hovered ? c.brand : c.text;

    // A chevron of two strokes: right when folded, down when open.
    const x = r.x + 2;
    const h = CHEVRON / 2;
    if (this.expanded) {
      rc.drawLine({ x, y: mid - h / 2 }, { x: x + h, y: mid + h / 2 }, 2, ink);
      rc.drawLine({ x: x + h, y: mid + h / 2 }, { x: x + CHEVRON, y: mid - h / 2 }, 2, ink);
    } else {
      rc.drawLine({ x: x + h / 2, y: mid - h }, { x: x + h * 1.5, y: mid }, 2, ink);
      rc.drawLine({ x: x + h * 1.5, y: mid }, { x: x + h / 2, y: mid + h }, 2, ink);
    }

    const titleX = x + CHEVRON + GAP.md;
    rc.drawText(this.#title, titleX, mid, FONT.h2, ink, { baseline: 'middle' });
    if (this.summary) {
      const width = rc.measureText(this.summary, FONT.small).width;
      const titleEnd = titleX + rc.measureText(this.#title, FONT.h2).width + GAP.lg;
      const sx = r.x + r.width - width;
      if (sx > titleEnd) rc.drawText(this.summary, sx, mid, FONT.small, c.subtleText, { baseline: 'middle' });
    }
  }
}

export interface Collapsible {
  readonly card: Card;
  /** Where the folded content goes. */
  readonly body: ReturnType<typeof vstack>;
  readonly expanded: boolean;
  setExpanded(expanded: boolean): void;
  /** Small text at the heading's right-hand end - "6 settings", say - shown while there is room. */
  setSummary(summary: string): void;
}

export function collapsible(title: string, options: { expanded?: boolean } = {}): Collapsible {
  const shell = card();
  const stack = vstack({ spacing: GAP.md });
  const body = vstack({ spacing: GAP.md });
  let expanded = options.expanded ?? false;

  const header = new CollapsibleHeader({ title, onToggle: () => set(!expanded) });
  stack.controls.add(header);
  stack.controls.add(body);
  shell.controls.add(stack);

  function set(next: boolean): void {
    expanded = next;
    header.expanded = next;
    body.visible = next;
    header.invalidate();
    relayout(body.visible ? body : stack);
  }
  header.expanded = expanded;
  body.visible = expanded;

  return {
    card: shell,
    body,
    get expanded() {
      return expanded;
    },
    setExpanded(next: boolean) {
      if (next !== expanded) set(next);
    },
    setSummary(summary: string) {
      header.summary = summary;
      header.invalidate();
    }
  };
}
